import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import { ReceivingTrade } from '../ActiveReceivingPage';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { useCompleteTradeMutation } from 'store/services/trade/tradeApi';
import { useSubmitTradeRatingMutation } from 'store/services/trade/tradeRatingApi';
import { useGetUiPreferencesQuery, useUpdateUiPreferencesMutation } from 'store/services/settings/uiPreferencesApi';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { toast } from 'react-toastify';
import { ContactUserModal } from 'components/ContactUserModal/ContactUserModal';
import SendPointsModal from 'components/SendPointsModal/SendPointsModal';
import FirstTradeConfirmModal from './FirstTradeConfirmModal';
import { ReceivingTradeItem } from '../ActiveReceivingPage';
import { AssetUrls } from 'config/assets';
import { AvatarWithFlag } from 'components/AvatarWithFlag';
import { SetIcon } from 'components/SetIcon';
import { FoilCardImage } from 'components/FoilCardImage';
import './ReceivingTradeCard.css';

interface Props {
  trade: ReceivingTrade;
  onFirstTradeCompleted?: (shareImageUrl: string | null, cards: ReceivingTradeItem[]) => void;
}

interface Ratings {
  speed: number;
  accuracy: number;
  communication: number;
}

// Format condition to short acronym
const formatCondition = (condition: string): string => {
  const conditionMap: { [key: string]: string } = {
    'NEAR_MINT': 'NM',
    'NM': 'NM',
    'SLIGHTLY_PLAYED': 'LP',
    'LIGHTLY_PLAYED': 'LP',
    'LP': 'LP',
    'SP': 'LP',
    'MODERATELY_PLAYED': 'MP',
    'MP': 'MP',
    'HEAVILY_PLAYED': 'HP',
    'HP': 'HP',
    'DAMAGED': 'DMG',
    'DMG': 'DMG'
  };
  return conditionMap[condition] || condition;
};

// Format language code to short code (normalize variations)
const formatLanguage = (language: string): string => {
  const languageMap: { [key: string]: string } = {
    'EN': 'EN',
    'ENGLISH': 'EN',
    'FR': 'FR',
    'FRENCH': 'FR',
    'DE': 'DE',
    'GERMAN': 'DE',
    'ES': 'ES',
    'SPANISH': 'ES',
    'IT': 'IT',
    'ITALIAN': 'IT',
    'PT': 'PT',
    'PORTUGUESE': 'PT',
    'JP': 'JP',
    'JA': 'JP',
    'JAPANESE': 'JP',
    'KR': 'KR',
    'KO': 'KR',
    'KOREAN': 'KR',
    'RU': 'RU',
    'RUSSIAN': 'RU',
    'CN': 'CN',
    'ZH': 'CN',
    'CHINESE': 'CN',
    'TW': 'TW',
    'NL': 'NL',
    'DUTCH': 'NL',
    'PL': 'PL',
    'POLISH': 'PL',
    'CS': 'CS',
    'CZECH': 'CS',
    'HU': 'HU',
    'HUNGARIAN': 'HU'
  };
  return languageMap[language.toUpperCase()] || language.toUpperCase().slice(0, 2);
};

export default function ReceivingTradeCard({ trade, onFirstTradeCompleted }: Props) {
  const [ratings, setRatings] = useState<Ratings>({
    speed: trade.speed,
    accuracy: trade.accuracy,
    communication: trade.communication
  });
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isSendPointsOpen, setIsSendPointsOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completeTrade, { isLoading: isCompletingTrade }] = useCompleteTradeMutation();
  const [submitRating] = useSubmitTradeRatingMutation();
  const { data: uiPrefs } = useGetUiPreferencesQuery();
  const [updateUiPreferences] = useUpdateUiPreferencesMutation();
  const navigate = useNavigate();

  // Debounce rating changes (1 second delay)
  const debouncedRatings = useDebouncedValue(ratings, 1000);

  // Track if this is the initial mount (skip auto-submit on load)
  const isInitialMount = useRef(true);
  const hasUserChangedRating = useRef(false);

  // Submit rating when debounced value changes (user clicked stars)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (trade.status === 'completed' && hasUserChangedRating.current) {
      submitRating({
        tradeId: trade.id,
        speed: debouncedRatings.speed,
        accuracy: debouncedRatings.accuracy,
        communication: debouncedRatings.communication,
      }).catch((error) => {
        console.error('Failed to submit rating:', error);
        toast.error('Failed to save rating');
      });
    }
  }, [debouncedRatings, trade.id, trade.status, submitRating]);

  const executeTradeCompletion = async () => {
    try {
      const tradeId = String(trade.id);
      const result = await completeTrade({ tradeId }).unwrap();

      if (result.success) {
        // Show fee information if available
        if (result.tradeFee && result.tradeFee > 0) {
          toast.success(`Trade completed! ${result.pointsCredited || 0} points credited to sender (${result.tradeFee} point fee deducted)`);
        } else {
          toast.success(`Trade completed! ${result.pointsCredited || 0} points credited to sender.`);
        }

        // Auto-submit 5-star rating on trade completion
        submitRating({
          tradeId: String(trade.id),
          speed: 5,
          accuracy: 5,
          communication: 5,
        }).catch((error) => {
          console.error('Failed to auto-submit rating:', error);
        });

        // Notify parent for first trade celebration (lifted to page level to survive unmount)
        if (result.isFirstTrade && !uiPrefs?.hasSeenFirstTradeCelebration) {
          updateUiPreferences({ hasSeenFirstTradeCelebration: true });
          onFirstTradeCompleted?.(result.shareImageUrl || null, trade.items);
        }
      } else {
        toast.error(result.message || 'Failed to complete trade');
      }
    } catch (error: any) {
      console.error('Error completing trade:', error);
      if (error?.status === 500) {
        toast.error('Server error occurred. The backend may need to be checked.');
      } else {
        toast.error(error?.data?.message || 'Failed to complete trade');
      }
    }
  };

  const handleCompleteTrade = () => {
    // Check if trade is in a valid status to complete
    if (trade.status !== 'transit' && trade.status !== 'preparing') {
      toast.warning(`Cannot complete trade in ${trade.status} status.`);
      return;
    }

    // Show first-time confirmation modal if user hasn't seen it
    // If preferences are still loading (undefined), skip the modal to avoid blocking
    if (uiPrefs && !uiPrefs.hasSeenTradeConfirmModal) {
      updateUiPreferences({ hasSeenTradeConfirmModal: true });
      setShowConfirmModal(true);
      return;
    }

    executeTradeCompletion();
  };

  const handleConfirmArrived = () => {
    setShowConfirmModal(false);
    executeTradeCompletion();
  };

  const handleContact = () => {
    setIsContactModalOpen(true);
  };

  const handleSayThanks = () => {
    setIsContactModalOpen(true);
  };

  const handleTradeAgain = () => {
    // Navigate to /sendcards with the trader's username as search filter
    navigate(`/sendcards?search=${encodeURIComponent(trade.traderName)}`);
  };
  const getStatusTitle = () => {
    switch (trade.status) {
      case 'preparing':
        return 'Confirmed';
      case 'transit':
        return 'Shipped';
      case 'completed':
        return 'Arrived';
      case 'cancelled':
        return trade.isRemovedItem ? 'Removed' : 'Cancelled';
      default:
        return '';
    }
  };

  const getStatusLabel = () => {
    switch (trade.status) {
      case 'preparing':
        return 'You will receive:';
      case 'transit':
        return 'You will receive:';
      case 'completed':
        return 'You received:';
      case 'cancelled':
        return trade.isRemovedItem ? 'You were receiving:' : 'You were receiving:';
      default:
        return '';
    }
  };

  const getTimeDisplay = () => {
    switch (trade.status) {
      case 'preparing':
        const timeToShip = Math.ceil(Math.random() * 3); // Mock calculation
        return `Time to ship: ${timeToShip} day${timeToShip > 1 ? 's' : ''}`;
      case 'transit':
        return `Estimated time to arrive: ${trade.estimatedDelivery}`;
      case 'completed':
        return `Arrived: ${trade.completed}`;
      default:
        return '';
    }
  };

  const handleStarClick = (category: keyof Ratings, starIndex: number) => {
    hasUserChangedRating.current = true;
    setRatings(prev => ({
      ...prev,
      [category]: starIndex
    }));
  };

  const renderStarRating = (category: keyof Ratings, rating: number) => {
    // Ensure rating is between 0 and 5
    const safeRating = Math.max(0, Math.min(5, rating || 0));
    const emptyStars = Math.max(0, 5 - safeRating);
    
    if (trade.status !== 'completed') {
      return '★'.repeat(safeRating) + '☆'.repeat(emptyStars);
    }

    return (
      <div className="interactive-stars">
        {[1, 2, 3, 4, 5].map(starIndex => (
          <span
            key={starIndex}
            className={`star ${starIndex <= ratings[category] ? 'filled' : 'empty'}`}
            onClick={() => handleStarClick(category, starIndex)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="receiving-trade-card">
      <div className="receiving-trade-card-left">
        <div className="package-title-row">
          <h3 className="trade-status-title">{getStatusTitle()}</h3>
          <Points points={trade.totalValue} type="big" />
        </div>
        
        <div className="trade-info">
          <div className="trade-detail">
            <span className="trade-label">Trade#</span>
            <span className="trade-value">{trade.tradeNumber}</span>
          </div>
          <div className="trade-detail">
            <span className="trade-label">Initiated:</span>
            <span className="trade-value">{trade.initiated}</span>
          </div>
          {trade.shipped && (
            <div className="trade-detail">
              <span className="trade-label">Shipped:</span>
              <span className="trade-value">{trade.shipped}</span>
            </div>
          )}
          {trade.completed && (
            <div className="trade-detail">
              <span className="trade-label">Completed:</span>
              <span className="trade-value">{trade.completed}</span>
            </div>
          )}
          {trade.cancelled && (
            <div className="trade-detail">
              <span className="trade-label">{trade.isRemovedItem ? 'Removed:' : 'Cancelled:'}</span>
              <span className="trade-value">{trade.cancelled}</span>
            </div>
          )}
          {trade.isRemovedItem && trade.removedFromTradeNumber && (
            <div className="trade-detail">
              <span className="trade-label">Removed from:</span>
              <span className="trade-value">Trade #{trade.removedFromTradeNumber}</span>
            </div>
          )}
        </div>

        <div className="time-display">
          <div className="time-label">{getTimeDisplay()}</div>
          {trade.status === 'preparing' && (
            <div className="progress-bar-container">
              <div className="progress-fill" style={{ width: '33%' }}></div>
            </div>
          )}
          {trade.status === 'transit' && (
            <div className="progress-bar-container">
              <div className="progress-fill" style={{ width: '66%' }}></div>
            </div>
          )}
        </div>

        {trade.status === 'completed' && (
          <div className="trader-rating">
            <div className="rating-row">
              <span className="rating-label">Speed:</span>
              <span className="rating-stars">{renderStarRating('speed', ratings.speed)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Accuracy:</span>
              <span className="rating-stars">{renderStarRating('accuracy', ratings.accuracy)}</span>
            </div>
            <div className="rating-row">
              <span className="rating-label">Communication:</span>
              <span className="rating-stars">{renderStarRating('communication', ratings.communication)}</span>
            </div>
          </div>
        )}

        <div className="trade-actions">
          {trade.status === 'preparing' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContact}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-completed.svg`}
                    alt="Complete"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isCompletingTrade ? "Completing..." : "Complete Trade"}
                color="purple"
                onPress={handleCompleteTrade}
                isDisabled={isCompletingTrade}
              />
            </>
          )}
          {trade.status === 'transit' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Contact"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Contact"
                color="dark-grey"
                onPress={handleContact}
              />
              <MagicButton
                icon={
                  <img
                    src={`${AssetUrls.base}/assets/icons/SendCards/icon-completed.svg`}
                    alt="Complete"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label={isCompletingTrade ? "Completing..." : "Complete Trade"}
                color="purple"
                onPress={handleCompleteTrade}
                isDisabled={isCompletingTrade}
              />
            </>
          )}
          {trade.status === 'completed' && (
            <>
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/contact.svg"
                    alt="Say thanks"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Say thanks"
                color="dark-grey"
                onPress={handleSayThanks}
              />
              <MagicButton
                icon={
                  <img
                    src="https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/logos/arevakhach-icon.svg"
                    alt="Trade Again"
                    style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }}
                  />
                }
                label="Trade again"
                color="dark-grey"
                onPress={handleTradeAgain}
              />
            </>
          )}
        </div>
      </div>

      <div className="receiving-trade-card-right">
        <div className="trader-info-row">
          <div className="status-label-left">
            <span className="status-text">{getStatusLabel()}</span>
          </div>
          <div className="trader-info">
            {trade.status === 'preparing' && (
              <button
                className="send-points-btn"
                onClick={() => setIsSendPointsOpen(true)}
              >
                <img
                  src={AssetUrls.icons.profile.coin}
                  alt=""
                  className="send-points-btn-icon"
                />
                Send Points
              </button>
            )}
            <AvatarWithFlag
              avatarUrl={trade.traderAvatar}
              username={trade.traderName}
              countryCode={trade.shippingAddress?.country}
              size={38}
              isElite={trade.traderIsElite}
              showAnimatedBorder={trade.traderShowAnimatedBorder}
              borderColor1={trade.traderBorderColor1}
              borderColor2={trade.traderBorderColor2}
            />
            <div className="trader-identity">
              <UsernameLink
                username={trade.traderName}
                isElite={trade.traderIsElite}
                className="trader-name trader-name--clickable"
              />
              <Points points={trade.pointBalance} type="small" />
            </div>
          </div>
        </div>

        <div className="items-table">
          <div className="table-header">
            <span className="col-qty">Qty</span>
            <span className="col-name">Name</span>
            <span className="col-set">Set</span>
            <span className="col-foil">Foil</span>
            <span className="col-condition">Condition</span>
            <span className="col-language">Language</span>
            <span className="col-points">Points</span>
          </div>
          
          {trade.items.map((item, index) => (
            <div key={index} className="table-row">
              <span className="col-qty">{item.qty}</span>
              <div className="col-name">
                <FoilCardImage
                  src={AssetUrls.cards.ensureCDNUrl(item.image)?.replace('/large/', '/small/')?.replace('/normal/', '/small/') || ''}
                  alt={item.name}
                  className="card-thumbnail"
                  foil={item.foil}
                  borderRadius="4px"
                />
                <span>{item.name}</span>
              </div>
              <div className="col-set" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <SetIcon setIconUrl={item.setIconUrl} setName={item.set} />
                <span>{item.setCode || item.set}{item.collectorNumber && <span style={{ color: '#888' }}> #{item.collectorNumber}</span>}</span>
              </div>
              <span className="col-foil">{item.foil ? 'Foil' : '—'}</span>
              <span className="col-condition">{formatCondition(item.condition)}</span>
              <span className="col-language">{formatLanguage(item.language)}</span>
              <div className="col-points">
                <Points points={(item.pointValue || 0) * item.qty} type="small" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <ContactUserModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        receiverId={parseInt(trade.traderId)}
        receiverUsername={trade.traderName}
        receiverAvatar={trade.traderAvatar}
        tradeId={trade.id}
      />

      <SendPointsModal
        isOpen={isSendPointsOpen}
        onClose={() => setIsSendPointsOpen(false)}
        recipientId={parseInt(trade.traderId)}
        recipientUsername={trade.traderName}
        tradeId={trade.id}
      />

      <FirstTradeConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmArrived}
        onCancel={() => setShowConfirmModal(false)}
        isCompletingTrade={isCompletingTrade}
      />
    </div>
  );
}